<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    @hide="onDialogHide"
  >
    <q-card class="q-dialog-plugin">
      <q-card-section class="row items-center">
        <div
          class="text-h6 ellipsis col"
        >
          {{ isCreating ? $t('programs.workflows.editWorkflow.textAddStatus')
            : $t('programs.workflows.statusInfo.textTitle') }}
        </div>
        <q-btn
          v-close-popup
          flat
          round
          dense
          icon="close"
        />
      </q-card-section>
      <q-card-section class="row justify-between">
        <q-btn
          icon="colorize"
          size="sm"
          class="col-2"
          :color="statusColor"
          :style="{ backgroundColor: statusColor }"
          :text-color="colorPickerIconColor"
        >
          <q-popup-proxy
            ref="colorPicker"
            transition-show="scale"
            transition-hide="scale"
          >
            <q-color
              v-model="statusColor"
              no-header
              default-view="palette"
            />
          </q-popup-proxy>
        </q-btn>
        <hr-icon-select
          v-model="statusIcon"
          :label="$t('programs.workflows.statusInfo.inputIconLabel')"
          :options="ICON_OPTIONS"
          class="col-3"
        />
        <q-input
          v-model="statusLabel"
          :label="$t('programs.workflows.statusInfo.inputLabelLabel')"
          autofocus
          maxlength="25"
          outlined
          class="col-6"
        />
      </q-card-section>
      <q-card-actions class="row">
        <q-btn
          v-if="!!status && !isCreating"
          :label="$t('programs.workflows.statusInfo.textRemoveStatus')"
          color="negative"
          flat
          @click="onRemove"
        />
        <q-space />
        <q-btn
          color="secondary"
          :label="$t('common.buttonCancel')"
          flat
          @click="onCancelClick"
        />
        <q-btn
          color="primary"
          :label="$t('common.buttonSave')"
          :disable="!canSubmit"
          flat
          @click="onOKClick"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
<script>
import { targetStatuses } from 'boot/globals';
import { chain, cloneDeep, find, get, keys, omitBy, set } from 'lodash';
import { HrIconSelect } from 'src/design-system';
import { getTextColor } from 'src/utils/TargetUtils';

const EXCLUDED_ICONS = ['check', 'cancel_presentation'];
const ICON_OPTIONS = [
  'star',
  'sym_o_check',
  'sym_o_autorenew',
  'sym_o_do_not_disturb',
  'sym_o_expand',
  'sym_o_stacks',
  'sym_o_step',
  'sym_o_category_search',
  'sym_o_step_over',
  'sym_o_timer_arrow_down',
  'sym_o_timer_arrow_up',
  'groups',
  'person_add_alt_1',
  'rocket_launch',
  'front_hand',
  'sym_o_partner_exchange',
  'calendar_month',
  'build',
  'celebration',
  'sym_o_target',
  'sym_o_checklist',
  'house',
  'add_home',
  'emoji_transportation',
  'sym_o_book_2',
  ...Object.values(targetStatuses)
    .filter(({ icon }) => !EXCLUDED_ICONS.includes(icon))
    .map(({ icon }) => icon),
];

export default {
  name: 'EditWorkflowStatus',
  components: { HrIconSelect },

  props: {
    // ...your custom props
    statusInfo: {
      type: Object,
      required: true,
    },
    isCreating: {
      type: Boolean,
      default: false,
    },
    editWorkflowStatuses: {
      type: Object,
      default: null,
    },
  },

  emits: ['hide', 'ok'],

  data() {
    return {
      ICON_OPTIONS,
      editStatusInfo: {},
      colorPickerIconColor: 'white',
    };
  },

  computed: {

    status() {
      return get(this.statusInfo, 'value');
    }, // status()

    statusColor: {
      get() {
        return get(this.editStatusInfo, 'color', targetStatuses.acquisition.color);
      },
      set(value) {
        return set(this.editStatusInfo, 'color', value);
      },
    }, // statusColor()

    statusIcon: {
      get() {
        return get(this.editStatusInfo, 'icon', targetStatuses.acquisition.icon);
      },
      set(value) {
        return set(this.editStatusInfo, 'icon', value);
      },
    }, // statusIcon()

    statusLabel: {
      get() {
        return get(this.editStatusInfo, 'label');
      },
      set(value) {
        return set(this.editStatusInfo, 'label', value);
      },
    }, // statusLabel()

    canSubmit() {
      const labelLength = chain(this.statusLabel).trim().size().value();
      const hasValidLabelLength = labelLength > 1 && labelLength <= 25;

      if (!this.editWorkflowStatuses) {
        return hasValidLabelLength;
      }

      const statusId = this.generateStatusId(this.statusLabel);
      return hasValidLabelLength && !keys(this.editWorkflowStatuses).includes(statusId);
    },
  },

  watch: {
    statusInfo: {
      immediate: true,
      deep: true,
      handler(newStatusInfo) {
        const statusInfoClone = cloneDeep(newStatusInfo);

        if (this.isCreating) {
          statusInfoClone.label = '';
        }

        this.editStatusInfo = statusInfoClone;
      },
    },

    statusColor: {
      immediate: true,
      handler(newStatusColor) {
        this.colorPickerIconColor = getTextColor(newStatusColor);
      },
    },
  },

  methods: {

    show() {
      this.$refs.dialog.show();
    },

    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      this.$emit('hide');
    },

    async onOKClick() {
      const systemStatus = find(targetStatuses, { value: this.status });
      const statusInfo = omitBy(this.editStatusInfo, (value, prop) => (value === get(systemStatus, prop)));
      this.$emit('ok', statusInfo);
      this.hide();
    },

    onCancelClick() {
      // we just need to hide dialog
      this.hide();
    },

    onRemove() {
      const statusLabel = get(this.editStatusInfo, 'label');
      if (!this.status || this.isCreating) {
        return;
      }
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t(
            'programs.workflows.statusInfo.confirmRemoveStatus',
            { statusLabel },
          ),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          this.$emit('ok', { isDeleted: true });
          this.hide();
        });
    }, // onRemove()

    generateStatusId(statusLabel) {
      return chain(statusLabel)
        .trim()
        .toLower()
        .replace(/[^a-z0-9]/gi, '')
        .value();
    }, // generateStatusId()
  },
};
</script>
